import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Transition from '../utils/Transition';
import { useUser } from '../userContext'; // Import the useUser hook
// Avatar always shows user initial — never businessProfile.headlinePhoto
// (which is the company logo and was leaking game assets into account UI)
function DropdownProfile({ align, handleSignOut }) {
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const trigger = useRef(null);
  const dropdown = useRef(null);

  // Get user and business data from the context
  const { businessProfile, userProfile, isOwner } = useUser();
  const businessName = businessProfile?.name || '';
  const userName = userProfile?.firstName || 'Please update your profile details';

  // Get the first initial from the user's name for fallback avatar
  const userInitial = userName.charAt(0).toUpperCase();

  // Close on click outside
  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!dropdown.current) return;
      if (!dropdownOpen || dropdown.current.contains(target) || trigger.current.contains(target)) return;
      setDropdownOpen(false);
    };
    document.addEventListener('click', clickHandler);
    return () => document.removeEventListener('click', clickHandler);
  });

  // Close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!dropdownOpen || keyCode !== 27) return;
      setDropdownOpen(false);
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  });

  return (
    <div className="relative inline-flex">
      <button
        ref={trigger}
        className="w-11 h-11 inline-flex justify-center items-center rounded-[var(--admin-card-radius)] transition-all duration-200 gch-input-ghost gch-text-secondary hover:text-white border border-transparent hover:border-white/20"
        aria-haspopup="true"
        onClick={() => setDropdownOpen(!dropdownOpen)}
        aria-expanded={dropdownOpen}
      >
        <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-sm font-semibold bg-[var(--admin-cta-solid)]">
            {userInitial}
          </div>
      </button>

      <Transition
        className={`origin-top-right z-[9999] absolute top-full min-w-48 py-2 px-1.5 rounded-[var(--admin-card-radius)] mt-1 gch-card ${align === 'right' ? 'right-0' : 'left-0'}`}
        show={dropdownOpen}
        enter="transition ease-out duration-200 transform"
        enterStart="opacity-0 -translate-y-2"
        enterEnd="opacity-100 translate-y-0"
        leave="transition ease-out duration-200"
        leaveStart="opacity-100"
        leaveEnd="opacity-0"
      >
        <div
          ref={dropdown}
          className="pb-0.5"
          onFocus={() => setDropdownOpen(true)}
          onBlur={() => setDropdownOpen(false)}
        >
          <div className="pt-2 pb-2 px-3 mb-1 border-b border-white/10">
            <div className="font-semibold gch-text-primary">{businessName}</div>
            <div className="text-xs font-medium gch-text-secondary">
              {isOwner ? 'Admin: ' : 'User: '}{userName}
            </div>
          </div>
          <ul className="space-y-0.5">
            <li>
              <Link
                className="font-semibold text-sm py-2.5 px-3 transition-all duration-200 gch-text-secondary hover:bg-white/10 hover:text-white rounded-lg block text-left"
                to="/studio/settings/account"
                onClick={() => setDropdownOpen(!dropdownOpen)}
              >
                Settings
              </Link>
            </li>
            <li>
              <button
                type="button"
                className="w-full font-semibold text-sm py-2.5 px-3 transition-all duration-200 gch-text-secondary hover:bg-red-500/20 hover:text-red-300 rounded-lg text-left"
                onClick={() => {
                  setDropdownOpen(!dropdownOpen);
                  handleSignOut();
                }}
              >
                Sign Out
              </button>
            </li>
          </ul>
        </div>
      </Transition>
    </div>
  );
}

export default DropdownProfile;